import { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Image from './Image';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import { useTranslation } from 'react-i18next';
import useFetchDocument from '../hooks/useFetchDocument';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import { getPollData, submitPollData } from '../services/Api';
import { u } from '../common/Units';
import { TaskBlock, TaskCard, TaskCardImage, TaskCardContent, TaskQuestion, TaskAnswers, TaskAnswer, TaskAnswerLetter, TaskTextButton, answerLetter } from './TaskCard';

interface BlockPollProps {
  pollData?: any[];
}

interface PollItemProps {
  pollItem: any;
}

interface PollApiResponse {
  updated_at: string;
  created_at: string;
  document_id: number;
  show_result: boolean;
  poll_data: Record<string, number>;
}

const Votes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 1.1em;
`;

const Vote = styled.li`
  & + & {
    margin-top: ${u(20)};
  }

  &.selected {
    font-weight: 600;
  }
`;

const VoteLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${u(8)};

  & > :last-child {
    margin-left: ${u(16)};
  }
`;

const VoteGraph = styled.div`
  height: ${u(16)};
  border-radius: ${u(8)};
  background-color: var(--task-tint);
  overflow: hidden;
`;

const VoteGraphInner = styled.div<{ $percentage?: number }>`
  height: 100%;
  background-color: ${p => p.theme.accentColor};
  transform-origin: left;
  transform: scaleX(${p => (p.$percentage || 0) / 100});
  transition: transform 0.8s ease;
`;

const VoteCount = styled.p`
  margin: ${u(24)} 0 0;
  opacity: 0.8;
`;

/**
 * Renders all poll blocks.
 */
const BlockPoll: FC<BlockPollProps> = ({ pollData }) => {
  if (!pollData?.length) return null;

  return (
    <>
      {pollData.map((item: any, i: number) => (
        <PollItem pollItem={item} key={`poll${item.to_document_id || i}`} />
      ))}
    </>
  );
};

/**
 * Renders a single poll: answer buttons, or the results once the visitor has voted or chosen to see them.
 * Votes are kept in answeredPolls (shared, so the poll shows results wherever it appears), and everything
 * resets in idle mode.
 */
const PollItem: FC<PollItemProps> = ({ pollItem }) => {
  const { t } = useTranslation();
  const { currentLanguage, previewIsActive, answeredPolls, setAnsweredPolls, idleResetCount } = useContext(AppContext);
  const documentId: number = pollItem.to_document_id;
  const [currentMedia, setCurrentMedia] = useState<any>(null);
  const [showResultsWithoutVote, setShowResultsWithoutVote] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [chosenId, setChosenId] = useState<string>();
  const [results, setResults] = useState<Record<string, number>>({});
  const [pollData] = useFetchDocument(documentId, pollItem.previewData);

  const isAnswered = showResultsWithoutVote || Boolean(answeredPolls?.includes(documentId));

  const totalVotes = useMemo(() => Object.values(results).reduce((sum, count) => sum + Number(count || 0), 0), [results]);

  /** Share of the votes for an answer, in percent */
  const getPercentage = (answerId: string): number => totalVotes ? Math.round(Number(results[answerId] || 0) / totalVotes * 100) : 0;

  // Fetch the results once the poll is answered
  useEffect(() => {
    if (!isAnswered || !documentId) { return; }
    let isActive = true;
    (getPollData(String(documentId)) as Promise<PollApiResponse | false | undefined>).then(data => {
      isActive && setResults(data && data.poll_data ? data.poll_data : {});
    });
    return () => { isActive = false; };
  }, [isAnswered, documentId]);

  // Start over when idle mode resets the kiosk (the app empties answeredPolls)
  useEffect(() => {
    setShowResultsWithoutVote(false);
    setChosenId(undefined);
    setResults({});
  }, [idleResetCount]);

  /**
   * Submit a vote (answer.answerId is the response key). The results are fetched when the poll counts as answered.
   */
  const processAnswer = async (answer: any) => {
    if (isSubmitting || !documentId || !answer?.answerId) { return; }
    setIsSubmitting(true);
    setChosenId(answer.answerId);
    await submitPollData(String(documentId), answer.answerId);
    setAnsweredPolls((prev: number[]) => prev.includes(documentId) ? prev : [...prev, documentId]);
    setIsSubmitting(false);
  };

  if (pollData?.status !== 'published' && !previewIsActive) return null;

  const image = pollData?.content?.general?.image;
  const answers: any[] = (pollData?.content?.answers || [])
    .map((answer: any) => ({ ...answer, text: getTranslation(answer.answer, currentLanguage) }))
    .filter((answer: any) => answer.text?.length);

  return (
    <TaskBlock className="showInPreview">
      <TaskCard className={image?.mediaId ? "withImage" : undefined}>
        {image?.mediaId && (
          <LightboxWrapper>
            <LightboxItem media={currentMedia}>
              <TaskCardImage>
                <Image media={image} setMedia={setCurrentMedia}/>
              </TaskCardImage>
            </LightboxItem>
          </LightboxWrapper>
        )}

        <TaskCardContent>
          <TaskQuestion>{getTranslation(pollData?.content?.general?.question, currentLanguage)}</TaskQuestion>

          {!isAnswered ? (
            <TaskAnswers>
              {answers.map((answer, i) => (
                <TaskAnswer key={`answer${answer.answerId || i}`} className={chosenId === answer.answerId ? "selected" : undefined}
                  disabled={isSubmitting} onClick={() => processAnswer(answer)}>
                  <TaskAnswerLetter aria-hidden="true">{answerLetter(i)}</TaskAnswerLetter>
                  <span>{answer.text}</span>
                </TaskAnswer>
              ))}

              {pollData?.content?.settings?.showResultsWithoutVoting && answers.length > 0 && (
                <TaskTextButton onClick={() => setShowResultsWithoutVote(true)} disabled={isSubmitting}>{t('votes.seeResult')}</TaskTextButton>
              )}
            </TaskAnswers>
          ) : (
            <Votes>
              {answers.map((answer, i) => {
                const percentage = getPercentage(answer.answerId);
                return (
                  <Vote key={`result${answer.answerId || i}`} className={chosenId === answer.answerId ? "selected" : undefined}>
                    <VoteLabel>
                      <span>{answer.text}</span>
                      <span>{percentage}%</span>
                    </VoteLabel>
                    <VoteGraph>
                      <VoteGraphInner $percentage={percentage}/>
                    </VoteGraph>
                  </Vote>
                );
              })}
              <VoteCount>{t('votes.total', { count: totalVotes })}</VoteCount>
            </Votes>
          )}
        </TaskCardContent>
      </TaskCard>
    </TaskBlock>
  );
};

export default BlockPoll;